import React, { useContext } from "react";
import { Helmet } from "react-helmet";
import siteConfig from "../../config/siteConfig";
import { PageContext } from "../../context/PageContext";
import { SiteSettingsContext } from "../../context/SiteSettingsContext";
import { SanityImageSourceExt } from "../../models/shared/SanityImageSourceExt";
import {
  isNullOrWhiteSpace,
  replaceWithUnicode,
  truncateString
} from "../../shared/string";
import { validImage } from "../../utilities/image";
import { PortableTextBody } from "../PortableText";
import { PortableTextBlock } from "../PortableText/PortableText";
import imageBuilder from "../sanity/imageBuilder";
import {
  ArticleJsonLdPageData,
  CommonJsonLdPageData,
  LocalBusinessJsonLdPageData,
  useJsonLd
} from "./useJsonLd";

/* eslint-disable camelcase */

export enum PageSeoType {
  Article,
  Organization,
  LocalBusiness,
  Event,
  None
}

type SanitySeoProperties = {
  metaTitle?: string;
  metaDescription?: string;
  metaCanonicalUrl?: string;
  metaRobotsNoindex?: boolean;
  metaRobotsNoFollow?: boolean;
  metaRobotsNoArchive?: boolean;
};

type SanityOgProps = {
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: SanityImageSourceExt;
};

type SeoPageData = {
  seo?: SanitySeoProperties;
  socialMedia?: SanityOgProps;
  title?: string;
  preambleText?: string;
  preamble?: PortableTextBody | string;
  _rawPreamble?: PortableTextBody;
};

export type CommonSeoPageData = CommonJsonLdPageData & SeoPageData;

export type DocumentHeadSeoProps =
  | {
      pageSeoType: PageSeoType.Article;
      pageData: CommonSeoPageData & ArticleJsonLdPageData;
    }
  | {
      pageSeoType: PageSeoType.LocalBusiness;
      pageData: CommonSeoPageData & LocalBusinessJsonLdPageData;
    }
  | {
      pageSeoType: PageSeoType.Organization;
      pageData: CommonSeoPageData;
    }
  | {
      pageSeoType: PageSeoType.None;
      pageData: CommonSeoPageData;
    };

export type DocumentHeadProps = {
  canonical?: string;
} & DocumentHeadSeoProps;

function getMetaTags({ seo, socialMedia }: SeoPageData) {
  const { metaDescription, metaRobotsNoArchive, metaRobotsNoFollow, metaRobotsNoindex } =
    seo || {};

  const { ogDescription, ogImage, ogTitle } = socialMedia || {};

  let ogImageUrl: string | null = null;

  if (validImage(ogImage)) {
    ogImageUrl = imageBuilder(ogImage).url({ friendlyFilename: true });
  }

  const result: React.MetaHTMLAttributes<HTMLMetaElement>[] = [];

  if (!isNullOrWhiteSpace(metaDescription)) {
    result.push({
      name: "description",
      content: metaDescription
    });
  }

  result.push({
    property: "og:type",
    content: "website"
  });

  result.push({
    property: "og:site_name",
    content: "Anticimex"
  });

  if (!isNullOrWhiteSpace(ogTitle)) {
    result.push({
      name: "og:title",
      content: ogTitle
    });
  }

  if (!isNullOrWhiteSpace(ogDescription)) {
    result.push({
      name: "og:description",
      content: ogDescription
    });
  }

  if (!isNullOrWhiteSpace(ogImageUrl)) {
    result.push({
      name: "og:image",
      content: ogImageUrl
    });
  }

  if (metaRobotsNoArchive) {
    result.push({
      name: "robots",
      content: "noarchive"
    });
  }

  if (metaRobotsNoFollow) {
    result.push({
      name: "robots",
      content: "nofollow"
    });
  }

  if (metaRobotsNoindex) {
    result.push({
      name: "robots",
      content: "noindex"
    });
  }

  return result;
}

function getLinkTags({ seo }: SeoPageData) {
  const { metaCanonicalUrl } = seo || {};

  const result: React.LinkHTMLAttributes<HTMLLinkElement>[] = [];

  if (!isNullOrWhiteSpace(metaCanonicalUrl)) {
    result.push({
      rel: "canonical",
      href: metaCanonicalUrl
    });
  }

  return result;
}

const portableTextToPlainText = (
  blocks: PortableTextBlock[],
  delimiter = "\n\n"
): string =>
  blocks
    .filter(block => block._type === "block" && block.children?.length)
    .map(block => block.children.map(child => child.text).join(""))
    .join(delimiter);

const metaDescriptionLength = 158;

function trimAndTruncate(metaData: string) {
  if (isNullOrWhiteSpace(metaData)) {
    return null;
  }
  return truncateString(metaData.replace(/\s+/g, " ").trim(), metaDescriptionLength);
}

const DocumentHead = (props: DocumentHeadProps): JSX.Element => {
  const { canonical, pageData } = props;

  const { i18nLang, languages } = useContext(PageContext);
  const siteSettings = useContext(SiteSettingsContext);

  const {
    _rawPreamble,
    preamble,
    preambleText,
    seo,
    socialMedia,
    title: pageTitleRaw
  } = pageData || {};

  const pageTitle = replaceWithUnicode(pageTitleRaw);

  let metaTitle = seo?.metaTitle;

  if (isNullOrWhiteSpace(metaTitle)) {
    metaTitle = pageTitle;
  }
  if (isNullOrWhiteSpace(metaTitle)) {
    metaTitle = siteSettings.title;
  }

  let ogTitle = socialMedia?.ogTitle;

  if (isNullOrWhiteSpace(ogTitle)) {
    ogTitle = pageTitle;
  }
  if (isNullOrWhiteSpace(ogTitle)) {
    ogTitle = siteSettings.title;
  }

  const description = seo?.metaDescription;

  const jsonLd = useJsonLd({
    ...props,
    description,
    title: metaTitle
  });

  let resolvedMetaDescription: string | undefined;

  if (description) {
    resolvedMetaDescription = description;
  } else {
    let resolvedPreamble: string | undefined;

    if (preambleText) {
      resolvedPreamble = preambleText;
    } else if (typeof preamble === "string") {
      resolvedPreamble = preamble;
    } else {
      const portablePreamble = _rawPreamble ?? preamble;

      if (portablePreamble) {
        resolvedPreamble = portableTextToPlainText(portablePreamble, ". ");
      }
    }

    resolvedMetaDescription = resolvedPreamble
      ? trimAndTruncate(resolvedPreamble)
      : undefined;
  }

  const propsWithFallback = {
    seo: {
      ...seo,
      metaDescription: resolvedMetaDescription
    },
    socialMedia: {
      ...socialMedia,
      ogTitle
    }
  };

  let title = metaTitle;

  if (siteConfig.siteName?.length) {
    title = `${title} - ${siteConfig.siteName}`;
  }

  return (
    <Helmet
      htmlAttributes={{
        lang: i18nLang?.replace("_", "-")
      }}
    >
      <title data-react-helmet="true">{title}</title>

      {!!canonical?.length && !!siteConfig.baseUrl?.length && (
        <link
          key="canonical"
          data-react-helmet="true"
          rel="canonical"
          href={`${siteConfig.baseUrl}${canonical}`}
        />
      )}
      {!!languages?.length &&
        languages.map((lang, index) => (
          <link
            key={index}
            rel="alternate"
            hrefLang={
              lang.languageId === siteConfig.baseLang ? "x-default" : lang.languageId
            }
            href={`${siteConfig.baseUrl}${lang.currentPageLanguageUrl ?? ""}`}
          />
        ))}

      {/* Note: React Helmet does not support React fragments, hence the roundabout way of rendering meta and links */}
      {getMetaTags(propsWithFallback).map((metaProps, index) => (
        <meta key={index} {...metaProps} />
      ))}

      {getLinkTags(propsWithFallback).map((linkProps, index) => (
        <link key={index} {...linkProps} />
      ))}

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default DocumentHead;
